import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Select, Typography, Card, Space, Alert } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import type { 
  PlanogramTemplate, 
  ValidationError, 
  PlanType 
} from '../../../../types/plan-template';
import { ReferenceDataService } from '../../../../services/referenceDataService';
import { authService } from '../../../../services/authService';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface BasicInformationProps {
  templateData: Partial<PlanogramTemplate>;
  onDataChange: (updates: Partial<PlanogramTemplate>) => void;
  onValidationChange: (isValid: boolean, errors: ValidationError[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  mode: 'create' | 'edit';
  excludeId?: string;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  templateData,
  onDataChange,
  onValidationChange,
  mode,
  excludeId
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [businessActivities, setBusinessActivities] = useState<string[]>([]);
  const [userList, setUserList] = useState<any[]>([]);
  const [codeValidation, setCodeValidation] = useState<{
    status: 'validating' | 'success' | 'error' | '';
    message: string;
  }>({ status: '', message: '' });


  // Load business activities and user list
  useEffect(() => {
    loadBusinessActivities();
    loadUserList();
  }, []);

  // Initialize form with template data
  useEffect(() => {
    if (templateData) {
      form.setFieldsValue({
        code: templateData.code || '',
        name: templateData.name || '',
        owner: templateData.owner || undefined,
        description: templateData.description || '',
        businessActivity: templateData.businessActivity || undefined,
        remark: templateData.remark || ''
      });
    }
  }, [templateData, form]);

  const loadBusinessActivities = async () => {
    try {
      const response = await ReferenceDataService.getBusinessActivities();
      if (response.success) {
        setBusinessActivities(response.data);
      }
    } catch (error) {
      console.error('Failed to load business activities:', error);
    }
  };

  const loadUserList = async () => {
    try {
      const response = await authService.listAccount();
      if (response && response.data) {
        setUserList(response.data);
      }
    } catch (error) {
      console.error('Failed to load user list:', error);
    }
  };

  // Debounced code uniqueness check
  const checkCodeUniqueness = useCallback(
    debounce(async (code: string) => {
      if (!code || code.length < 3) {
        setCodeValidation({ status: '', message: '' });
        return;
      }

      setCodeValidation({ status: 'validating', message: t('planTemplate.wizard.basicInfo.validation.checkingCodeUniqueness') });

      try {
        // 代码唯一性检查功能已简化
        setCodeValidation({ 
          status: 'success', 
          message: t('planTemplate.wizard.basicInfo.validation.codeAvailable')
        });
      } catch (error) {
        setCodeValidation({ 
          status: 'error', 
          message: t('planTemplate.wizard.basicInfo.validation.checkCodeUniquenessFailed')
        });
      }
    }, 500),
    [excludeId]
  );

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    
    // Update template data
    onDataChange({
      code: values.code,
      name: values.name,
      owner: values.owner,
      description: values.description,
      businessActivity: values.businessActivity,
      remark: values.remark
    });

    // Validate form
    validateForm(values);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value;
    handleFormChange();
    
    if (code && code.length >= 3) {
      checkCodeUniqueness(code);
    } else {
      setCodeValidation({ status: '', message: '' });
    }
  };

  const validateForm = (values: any) => {
    const errors: ValidationError[] = [];

    // Validate code
    if (codeValidation.status === 'error') {
      errors.push({
        field: 'code',
        message: codeValidation.message,
        type: 'error'
      });
    }

    // Validate name
    if (!values.name) {
      errors.push({
        field: 'name',
        message: t('planTemplate.wizard.basicInfo.validation.nameRequired'),
        type: 'error'
      });
    } else if (values.name.length < 2) {
      errors.push({
        field: 'name',
        message: t('planTemplate.wizard.basicInfo.validation.nameMinLength'),
        type: 'error'
      });
    } else if (values.name.length > 50) {
      errors.push({
        field: 'name',
        message: t('planTemplate.wizard.basicInfo.validation.nameMaxLength'),
        type: 'error'
      });
    }

    // Validate description
    if (!values.description) {
      errors.push({
        field: 'description',
        message: t('planTemplate.wizard.basicInfo.validation.descriptionRequired'),
        type: 'error'
      });
    } else if (values.description.length < 10) {
      errors.push({
        field: 'description',
        message: t('planTemplate.wizard.basicInfo.validation.descriptionMinLength'),
        type: 'error'
      });
    } else if (values.description.length > 500) {
      errors.push({
        field: 'description',
        message: t('planTemplate.wizard.basicInfo.validation.descriptionMaxLength'),
        type: 'error'
      });
    }

    // Validate owner
    if (!values.owner) {
      errors.push({
        field: 'owner',
        message: t('ownerRequired'),
        type: 'error'
      });
    }

    // Validate business activity
    // if (!values.businessActivity) {
    //   errors.push({
    //     field: 'businessActivity',
    //     message: t('planTemplate.wizard.basicInfo.validation.businessActivityRequired'),
    //     type: 'error'
    //   });
    // }

    const isValid = errors.length === 0 && codeValidation.status !== 'validating';
    onValidationChange(isValid, errors);
  };

  const renderCodeValidationIcon = () => {
    switch (codeValidation.status) {
      case 'validating':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3}>{t('planTemplate.wizard.basicInfo.title')}</Title>
        <Paragraph type="secondary">
          {t('planTemplate.wizard.basicInfo.description')}
        </Paragraph>
      </div>

      {templateData.planType && (
        <Alert
          style={{ marginBottom: '24px' }}
          message={`${t('planTemplate.wizard.basicInfo.currentPlanType')}: ${templateData.planType}`}
          type="info"
          showIcon
        />
      )}

      <Card>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
          autoComplete="off"
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                name="name"
                label={t('planTemplate.wizard.basicInfo.templateName')}
                style={{ flex: 2 }}
                rules={[
                  { required: true, message: t('planTemplate.wizard.basicInfo.validation.nameRequired') },
                  { min: 2, message: t('planTemplate.wizard.basicInfo.validation.nameMinLength') },
                  { max: 50, message: t('planTemplate.wizard.basicInfo.validation.nameMaxLength') }
                ]}
              >
                <Input placeholder={t('planTemplate.wizard.basicInfo.namePlaceholder')} />
              </Form.Item>
            </div>

            <Form.Item
               name="owner"
               label={t('planTemplate.templateOwner')}
               rules={[
                 { required: true, message: t('planTemplate.ownerRequired') }
               ]}
             >
               <Select
                 placeholder={t('planTemplate.ownerPlaceholder')}
                 showSearch
                 optionFilterProp="children"
                 filterOption={(input, option) =>
                   (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                 }
                 options={userList.map(user => ({
                   value: user.value,
                   label: `${user.label} (${user.value})`
                 }))}
               />
             </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
            </div>

            {/* <Form.Item
              name="businessActivity"
              label={t('planTemplate.wizard.basicInfo.businessActivity')}
              rules={[{ required: true, message: t('planTemplate.wizard.basicInfo.validation.businessActivityRequired') }]}
            >
              <Select
                placeholder={t('planTemplate.wizard.basicInfo.businessActivityPlaceholder')}
                options={businessActivities.map(activity => ({
                  value: activity,
                  label: activity
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item> */}

            <Form.Item
              name="description"
              label={t('planTemplate.wizard.basicInfo.templateDescription')}
              rules={[
                { required: true, message: t('planTemplate.wizard.basicInfo.validation.descriptionRequired') },
                { min: 10, message: t('planTemplate.wizard.basicInfo.validation.descriptionMinLength') },
                { max: 500, message: t('planTemplate.wizard.basicInfo.validation.descriptionMaxLength') }
              ]}
            >
              <TextArea
                rows={4}
                placeholder={t('planTemplate.wizard.basicInfo.descriptionPlaceholder')}
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item
              name="remark"
              label={t('planTemplate.wizard.basicInfo.remark')}
            >
              <TextArea
                rows={3}
                placeholder={t('planTemplate.wizard.basicInfo.remarkPlaceholder')}
                showCount
                maxLength={200}
              />
            </Form.Item>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default BasicInformation;